import React from "react";
import { rgbToHex, Stack, styled, SxProps } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperClass } from "swiper/types";
import SliderControl from "../Sliders/SliderControl";
import SliderPagination from "../Sliders/SliderPagination";
import "swiper/css";
import { hexToRgbString } from "../../Utils/Helpers/cssUtils";

export interface SliderProps {
  sx?: SxProps;
  slidesPerView?: number;
  slidesPerGroup?: number;
  spaceBetween?: number;
  showControls?: boolean;
  paginate?: boolean;
  children: React.ReactNode;
  background?: "default" | "paper";
}

const SliderWrapper: React.FC<SliderProps> = (props) => {
  const {
    slidesPerGroup,
    slidesPerView = "auto",
    showControls = true,
    paginate = true,
    background = "default",
    children,
    spaceBetween,
    sx = {},
  } = props;
  const swiperRef = React.useRef<SwiperClass | null>(null);

  const [prevControlVisible, setPrevControlVisible] = React.useState(false);
  const [nextControlVisible, setNextControlVisible] = React.useState(false);
  const [pagination, setPagination] = React.useState({
    current: 0,
    total: 0,
  });

  const updatePagination = React.useCallback((swiper: SwiperClass) => {
    setPagination({
      current: swiper.snapIndex,
      total: swiper.snapGrid.length,
    });
  }, []);

  const handleSwiper = React.useCallback(
    (swiper: SwiperClass) => {
      swiperRef.current = swiper;
      updatePagination(swiper);
    },
    [updatePagination]
  );

  const handleClickNext = React.useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.slideNext();
  }, []);

  const handleClickPrev = React.useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.slidePrev();
  }, []);

  const handleSnapIndexChange = React.useCallback(
    (swiper: SwiperClass) => {
      if (swiper.snapGrid.length === 1) {
        return;
      }

      setPrevControlVisible(swiper.progress > 0);
      setNextControlVisible(swiper.progress < 1);
      updatePagination(swiper);
    },
    [updatePagination]
  );

  return (
    <Stack sx={{ position: "relative" }}>
      <StyledSlider
        sx={sx}
        onSwiper={handleSwiper}
        onSnapIndexChange={handleSnapIndexChange}
        slidesPerView={slidesPerView}
        slidesPerGroup={slidesPerGroup}
        spaceBetween={spaceBetween}
        allowTouchMove={false}
        controlsVisible={{ prev: prevControlVisible, next: nextControlVisible }}
        //updateOnImagesReady={false}
      >
        {children}
      </StyledSlider>
      {showControls && (
        <>
          <SliderControlWrapper
            controlType="prev"
            swiperControlVisible={prevControlVisible}
            paginate={paginate}
            background={background}
          >
            <SliderControl
              controlType="prev"
              onClick={handleClickPrev}
              disabled={!prevControlVisible}
              className="slider-control"
            />
          </SliderControlWrapper>
          <SliderControlWrapper
            controlType="next"
            swiperControlVisible={nextControlVisible}
            paginate={paginate}
            background={background}
          >
            <SliderControl
              controlType="next"
              onClick={handleClickNext}
              disabled={!nextControlVisible}
              className="slider-control"
            />
          </SliderControlWrapper>
        </>
      )}
      {paginate && <SliderPagination current={pagination.current} total={pagination.total} />}
    </Stack>
  );
};

const Slider = React.memo(SliderWrapper);
export { Slider as default, SwiperSlide as Slide };

const StyledSlider = styled(Swiper, { shouldForwardProp: (prop) => prop !== "controlsVisible" })<{
  controlsVisible: { prev: boolean; next: boolean };
}>(({ theme, controlsVisible }) => ({
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  maskImage: `linear-gradient(to right, rgba(0,0,0,${
    controlsVisible.prev ? "0" : ".9"
  }) 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 80%, rgba(0,0,0,${controlsVisible.next ? "0" : ".9"}) 100%)`,

  ".swiper-slide": {
    transform: "translate3d(0,0,0)",
    backfaceVisibility: "hidden",
  },
  ".swiper-wrapper": {
    backfaceVisibility: "hidden",
    transformStyle: "preserve-3d",
  },
}));

const SliderControlWrapper = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "sx" &&
    prop !== "background" &&
    prop !== "swiperControlVisible" &&
    prop !== "controlType" &&
    prop !== "paginate",
})<{
  swiperControlVisible: boolean;
  controlType: "prev" | "next";
  paginate: Required<SliderProps["paginate"]>;
  background: Required<SliderProps["background"]>;
}>(({ theme, swiperControlVisible, controlType, paginate, background }) => ({
  position: "absolute",
  top: 0,
  width: 124,
  bottom: paginate ? 82 : 0,
  zIndex: 2,
  visibility: swiperControlVisible ? "visible" : "hidden",
  opacity: 0,
  transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
  "&:hover": {
    opacity: swiperControlVisible ? 1 : 0,
  },
  ...(controlType === "prev" && {
    left: 0,
    ".slider-control": {
      zIndex: 3,
      left: theme.spacing(2),
      pointerEvents: "all",
    },
  }),
  ...(controlType === "next" && {
    right: -1,
    ".slider-control": {
      zIndex: 3,
      right: theme.spacing(2),
      pointerEvents: "all",
    },
  }),
}));
